import { Box, Tooltip } from '@mui/material'
import React, { useEffect } from 'react'
import { LuX } from 'react-icons/lu'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import CustomChip from '@/components/Chip'
import DentinRecipePieChart from '@/components/DentinPieChart'
import HtmlKonvaElement from '@/components/HtmlKonvaElement'
import Icon from '@/components/Icon'
import { useAppSelector } from '@/hooks/redux'
import { getInitials } from '@/pages/stainingStudio/utils'
import type { RootState } from '@/store'
import { initializeRecipeIndex, setRecipeIndex } from '@/store/appSlice'
import { DENTIN_BACKGROUNDS } from '@/utils/constants'
import { getDentinRecipe } from '@/utils/matisseV2/dentin'

import LabelValuePair from './LabelValuePair'

const TooltipContent = React.memo(
  ({
    output,
    keyName,
    dentinIndex,
    onIndexChange,
    setTooltipOpen,
  }: {
    output: any
    keyName: string
    dentinIndex: number
    onIndexChange: (key: string, value: number) => void
    setTooltipOpen: () => void
  }) => {
    const dentinRecipe = getDentinRecipe(output)
    const brand = output?.provided_input?.ceramicbrand

    return (
      <Box display="flex" flexDirection="column" gap={1} flexGrow={1}>
        {brand && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={1.5}
          >
            <LabelValuePair
              value={
                <Box
                  sx={{
                    wordBreak: 'break-word',
                    maxWidth: '100%',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {brand.toUpperCase() || ''}
                </Box>
              }
            />
            <Icon icon={LuX} size={18} onClick={setTooltipOpen} />
          </Box>
        )}

        {(dentinRecipe?.recipe_layer1?.[dentinIndex] ||
          dentinRecipe?.recipe_layer2?.[dentinIndex]) && (
          <>
            <Box sx={{ borderTop: '1px solid #999', width: '100%' }} />
            <DentinRecipePieChart
              showBrand={true}
              data={dentinRecipe}
              dentinIndex={dentinIndex}
              handleDentinIndexChange={(e, value) =>
                onIndexChange(keyName, value)
              }
              isSingleOnCanvas={
                !(dentinRecipe?.recipe_layer1 && dentinRecipe?.recipe_layer2)
              }
            />
          </>
        )}
      </Box>
    )
  },
)

const DentinTooltip = React.memo(
  ({
    keyName,
    data,
    output,
    open,
    toggle,
    close,
    workflow,
    onDelete,
  }: {
    keyName: string
    data: any
    output: any
    open: boolean
    toggle: (key: string) => void
    close: (key: string) => void
    workflow: 'fullLayering' | 'pfmCrown'
    onDelete: (key: string) => void
  }) => {
    const canvasImage = useSelector((state: RootState) => state.app.canvasImage)

    const location = useLocation()

    const getTooltipPlacement = (): 'right' | 'left' => {
      if (location.pathname === '/full-layering/dentin') {
        const region = data.output?.[keyName]
        if (region === 'CERVICAL') return 'right'
        if (region === 'MIDDLE') return 'left'
        if (region === 'INCISAL') return 'right'
        return 'left' // default fallback
      }
      return data.output?.[keyName] === 'INCISAL' ? 'right' : 'left'
    }

    const placement = getTooltipPlacement()

    const dispatch = useDispatch()
    const dentinRecipeIndexByRegion = useAppSelector(
      (state) => state?.app?.dentinRecipeIndexByRegion,
    )

    const imageId = canvasImage?.id

    const dentinIndex =
      dentinRecipeIndexByRegion?.[workflow]?.[imageId]?.[keyName] ?? 0

    useEffect(() => {
      const dentinRecipe = getDentinRecipe(output)
      const hasRecipe =
        (dentinRecipe?.recipe_layer1?.length ?? 0) > 0 ||
        (dentinRecipe?.recipe_layer2?.length ?? 0) > 0
      if (hasRecipe && imageId && keyName && workflow) {
        dispatch(initializeRecipeIndex({ workflow, imageId, region: keyName }))
      }
    }, [output, keyName, imageId])

    const handleDentinIndexChange = (region: string, value: number) => {
      dispatch(setRecipeIndex({ workflow, imageId, region, index: value }))
    }

    return (
      <HtmlKonvaElement key={keyName} x={data.position?.x} y={data.position?.y}>
        <Box position="relative">
          <Box sx={{ transform: 'translate(-50%, -60%)' }}>
            <Tooltip
              open={open}
              title={
                output ? (
                  <TooltipContent
                    output={output}
                    keyName={keyName}
                    dentinIndex={dentinIndex}
                    onIndexChange={handleDentinIndexChange}
                    setTooltipOpen={() => close(keyName)}
                  />
                ) : null
              }
              arrow
              placement={placement}
              slotProps={{
                popper: { disablePortal: true },
                tooltip: {
                  sx: {
                    bgcolor: 'rgba(33, 33, 33, 0.80)',
                    color: 'white',
                    padding: '10px',
                  },
                },
                arrow: { sx: { color: 'rgba(33, 33, 33, 0.80)' } },
              }}
            >
              <CustomChip
                onClick={() => toggle(keyName)}
                sx={{
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: 'black',
                  padding: '4px 8px',
                  background: DENTIN_BACKGROUNDS[keyName],
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  lineHeight: '20px',
                  '&:hover': {
                    background: DENTIN_BACKGROUNDS[keyName],
                  },
                  '& .MuiChip-label': {
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 8px 0 0px',
                    lineHeight: '20px',
                  },
                }}
                label={getInitials(data.output[keyName])}
                onDelete={() => onDelete(keyName)}
              />
            </Tooltip>
          </Box>
        </Box>
      </HtmlKonvaElement>
    )
  },
)

export default DentinTooltip
